import React, { useState, useRef, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import { ensureBoolean, getRemotedbUrl, i18nIntl, isNotEmpty, NSchange, parseVideo, preventSelection } from '../../lib/ep.js';
import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc';
import { size as _size , isEqual as _isEqual } from 'lodash';

import ActionButton, { CreateButton, SecondaryButton } from '../builder/ActionButton';
import ActionLink from '../builder/ActionLink';
import AnswerOptions from './AnswerOptions';
import ButtonCollection from '../builder/ButtonCollection.js';
import Checkbox from '../builder/Checkbox';
import DisabledArea from '../builder/DisabledArea';
import FloatingSelectorList from '../builder/FloatingSelectorList';
import Input from '../builder/Input';
import RichEditor from '../builder/RichEditor';
import MatrixSelector from '../builder/MatrixSelector';
import MediaUploader from '../builder/MediaUploader';
import Model from '../builder/Model';
import Range from '../builder/Range';
import Select from '../builder/Select';
import TextArea from '../builder/TextArea';

import mousekey from '../../hoc/keymouse';
import Tooltip from '../builder/Tooltip.js';
import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
const MKButton = mousekey(CreateButton);
const MKLink = mousekey(ActionLink,'li');

const SortableFields = SortableContainer(({items, intl, actions, open_answer, question, changeAnswer, image_hook }) => {
	return (
		<ul class="gui_sortable_list gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap">
			{items.map((value, index) => (
				<SortableItem
					key={`item-${index}`}
					index={index}
					answer={value}
					question={question}
					image_hook={image_hook}
					changeAnswer={(k, v) => changeAnswer(value.id, k, v)}
					open={(open_answer == value.id) ? true : false}
					actions={actions}
					intl={intl}
				/>
			))}
		</ul>
	);
});

const SortableItem = SortableElement(({answer, intl, actions, question, open, changeAnswer, image_hook }) => {
	return (
		<AnswerOptions
			answer={answer}
			question={question}
			intl={intl}
			actions={actions}
			changeAnswer={changeAnswer}
			open={open}
			image_hook={image_hook}
		/>
	)}
);

const MenuItem = ({e}) => (
	<MKLink
		onKeyMouse={e.mk}
		onMousePaste={e.mp}
		_class={{ icon:e.icon, other:((e.premium) ? 'is_addon_this' : '') }}
		onClick={e.action}
	>{e.label}</MKLink>
);

const QuestionOptions = (props) => {
	const i18n = (m) => i18nIntl(m, props.intl);

	const [ feedback, setFeedback ] = useState(false);
	const [ open_answer, setOpenAnswer ] = useState(props?.currentAnswer ?? false);
	const [ question, setQuestion ] = useState(props.question);
	const [ show_wrong, setShowWrong ] = useState(false);

	const base = (props.basename != undefined) ? `${props.basename}/` : '';

	const fields = [{
		icon:'knk-button',
		label:"Add a closed answer",
		action:() => addAnswer({ type:'closed_text' }),
		mk:(e) => keymouseQuestion(e, { type:'closed_text' }),
		mp:(type, text) => mousePasteAnswer(type, text, { type:'closed_text' }),
	}, {
		icon:'knk-rating',
		label:"Add a rating answer",
		action:() => addAnswer({ type:'rating' }),
		mk:(e) => keymouseQuestion(e, { type:'rating' }),
		mp:(type, text) => mousePasteAnswer(type, text, { type:'rating' }),
	}, {
		icon:'knk-open-text',
		label:"Add an open text answer",
		action:() => addAnswer({ type:'open_text' }),
		mk:(e) => keymouseQuestion(e, { type:'open_text' }),
		mp:(type, text) => mousePasteAnswer(type, text, { type:'open_text' }),
	}, {
		icon:'knk-options-list',
		label:"Add a list answer",
		action:() => addAnswer({ type:'list' }),
		mk:(e) => keymouseQuestion(e, { type:'list' }),
		mp:(type, text) => mousePasteAnswer(type, text, { type:'list' }),
	}];

	const multiple = useRef(false);

	const handleKeyUp = (e) => {
		if(e.keyCode != 91) return;
		multiple.current = false;
	};

	const handlePaste = (e) => {
		const items = (e.clipboardData || e.originalEvent.clipboardData).items;

		let blob = null;
		for(let i = 0; i < items.length; i++) {
			if(items[i].type.indexOf("image") === 0) {
				blob = items[i].getAsFile();
			}
		}

		if(blob == undefined) return;

		const reader = new FileReader();
		reader.onload = () => {};
		reader.readAsDataURL(blob);
	};

	useEffect(() => {
		document.addEventListener('keyup', handleKeyUp);
		document.addEventListener('paste', handlePaste);

		return () => {
			document.removeEventListener('keyup', handleKeyUp);
			document.removeEventListener('paste', handlePaste);	
		};
	}, []);

	useEffect(() => {
		if(question == props.question) return;
		setQuestion(props.question);
	}, [ props.question ]);

	const dispatchQuestion = (action) => {
		props.dispatch(action);
	};

	const changeQuestionConfig = (k, v) => {
		dispatchQuestion({
			k:`config.${k}`,
			qid:props.question.Question.id,
			type:'CHANGEQUESTION',
			v,
		});

		NSchange({ ...props.question.Question, config:{ ...props.question.Question.config } }, k, v);

		const _question = { ...props.question };
		_question.Question.config[k] = v;
		setQuestion(_question);
	};

	const changeQuestion = (k, v) => {
		if(k == 'isVertical') {
			v = (v == 'single') ? true : false;
		}

		if(k == 'full_image') {
			if(v === null) v = '';
			k = 'fullimage';
		}

		if(k == 'image' && v === null) {
			v = '';

			dispatchQuestion({
				k:'isVertical',
				qid:props.question.Question.id,
				type:'CHANGEQUESTION',
				v:true,
			});

			dispatchQuestion({
				k:'config.hidequestion',
				qid:props.question.Question.id,
				type:'CHANGEQUESTION',
				v:false,
			});
		}

		if(k == 'video') {
			if(v != '') {
				v = parseVideo(v);
			} else {
				dispatchQuestion({
					k:'config.hidequestion',
					qid:props.question.Question.id,
					type:'CHANGEQUESTION',
					v:false,
				});
			}
		}

		// ensure correct min and max values
		if(k == 'type') {
			switch(parseInt(v)) {
				case 1:
					dispatchQuestion({
						k:'min_answers',
						qid:props.question.Question.id,
						type:'CHANGEQUESTION',
						v:1,
					});
					NSchange(props.question.Question, 'min_answers', 1);

					dispatchQuestion({
						k:'max_answers',
						qid:props.question.Question.id,
						type:'CHANGEQUESTION',
						v:0,
					});
					NSchange(props.question.Question, 'max_answers', 0);

					dispatchQuestion({
						k:'multianswer',
						qid:props.question.Question.id,
						type:'CHANGEQUESTION',
						v:false,
					});
					NSchange(props.question.Question, 'multianswer', 0);

					dispatchQuestion({
						k:'multiorder',
						qid:props.question.Question.id,
						type:'CHANGEQUESTION',
						v:false,
					});
					NSchange(props.question.Question, 'multiorder', 0);
					break;

				case 2:
					dispatchQuestion({
						k:'multianswer',
						qid:props.question.Question.id,
						type:'CHANGEQUESTION',
						v:true,
					});
					NSchange(props.question.Question, 'multianswer', 1);

					dispatchQuestion({
						k:'multiorder',
						qid:props.question.Question.id,
						type:'CHANGEQUESTION',
						v:false,
					});
					NSchange(props.question.Question, 'multiorder', 0);
					break;

				case 3:
					dispatchQuestion({
						k:'multianswer',
						qid:props.question.Question.id,
						type:'CHANGEQUESTION',
						v:true,
					});
					NSchange(props.question.Question, 'multianswer', 1);

					dispatchQuestion({
						k:'multiorder',
						qid:props.question.Question.id,
						type:'CHANGEQUESTION',
						v:true,
					});
					NSchange(props.question.Question, 'multiorder', 1);
					break;
			}
		}

		//If there is feedback disable the button hiding
		if(k == 'feedback' && v != '' && props.question.Question.config.hidecontinue) {
			dispatchQuestion({
				k:'config.hidecontinue',
				qid:props.question.Question.id,
				type:'CHANGEQUESTION',
				v:false,
			});
		}

		//When modifiing the min answers adjust the minimum max_answers
		if(k == 'min_answers' && props.question.Question.max_answers < parseInt(v)) {
			dispatchQuestion({
				k:'max_answers',
				qid:props.question.Question.id,
				type:'CHANGEQUESTION',
				v,
			});
			NSchange(props.question.Question, 'max_answers', v);
		}

		dispatchQuestion({
			k,
			qid:props.question.Question.id,
			type:'CHANGEQUESTION',
			v,
		});

		NSchange(props.question.Question, k, v);

		if(k == 'question') {
			dispatchQuestion({
				k:'question_st',
				qid:props.question.Question.id,
				type:'CHANGEQUESTION',
				v,
			});
		}

		const _question = { ...props.question };
		_question.Question[k] = v;
		setQuestion(_question);
	};

	const addAnswer = (data, cb) => {
		const d = {
			answer:i18n("New answer"),
			question_id:props.question.Question.id,
			config:{
				type:data.type,
			},
		};

		//Rating answer
		if(data.type == 'rating') {
			d.config = {
				default_rate:3,
				img_off:"raty_icon ep-star-empty",
				img_on:"raty_icon ep-star",
				pre:1,
				star_count:5,
				type:data.type,
			};
		}

		//Open text answer
		if(data.type == 'open_text') {
			d.config = {
				type:data.type,
			};
		}

		//Open text answer
		if(data.type == 'list') {
			d.config = {
				correct_validation:'-',
				list_type:'list',
				type:data.type,
			};
		}

		if(data.p == undefined) data.p = [''];

		//Assign multiple creation
		d.p = data.p;

		$.post(getRemotedbUrl(`/quizs/addanswer/${props.question.Question.promotion_id}`), d, (res) => {
			if(!(res instanceof Array)) res = [res];

			props.dispatch({
				answers:res,
				qid:props.question.Question.id,
				type:'ADDANSWERS',
			})

			const _question = { ...question };
			for(let i in res) {
				_question.Answer.push(res[i]);
			}

			setQuestion(_question);

			if(cb !== undefined) {
				cb();
			} else {
				editAnswer(res[0]);
			}
		}, 'JSON');
	};

	const keymouseQuestion = (e, data) => {
		const num = parseInt(e.key);
		if(!isNaN(num)) {
			for(let i = 0; i < num; i++) {
				if(data.p == undefined) data.p = [];
				data.p.push("");
			}

			addAnswer(data);
		}
	};

	const mousePasteAnswer = (type, text, data) => {
		if(type == 'text') {
			data.p = text.split("\n");

			addAnswer(data, () => {
				if(props.onChange !== undefined) props.onChange();
			});
		}
	};

	const editAnswer = (answer) => {
		const _open_answer = (answer.id !== open_answer) ? answer.id : false;
		setOpenAnswer(_open_answer);

		if(answer.id != _open_answer) {
			window.location.hash = `${base}questions/${answer.question_id}/${answer.id}`;
		}
	};

	const copyAnswer = (answer, p) => {
		const data = {
			id:answer.id,
			answer:JSON.stringify(answer),
		};
		if(p !== undefined) data.p = p;

		const _question = { ...question };

		$.post('/quizs/copyanswer', data, (response) => {
			if(!(response instanceof Array)) response = [response];

			props.dispatch({
				answers:response,
				qid:answer.question_id,
				type:'ADDANSWERS',
			});

			for(let i in response) {
				_question.Answer.push(response[i]);
			}

			setQuestion(_question);
		}, 'JSON');
	};

	const chAnswer = (a, k, v) => {
		props.dispatch({
			aid:a.id,
			k,
			qid:question.Question.id,
			type:'CHANGEANSWER',
			v,
		});

		const b = NSchange(a, k, v, true);

		const _question = { ...question };
		for(let i in _question.Answer) {
			if(_question.Answer[i].id != a.id) continue;
			_question.Answer[i] = b;
			break;
		}

		setQuestion(_question);
	};

	const changeAnswer = (answer_id, k, v) => {
		const _question = { ...question };

		const a = _question.Answer.find((a) => a.id === answer_id);

		if(k == 'video') {
			if(v != '') {
				v = parseVideo(v);
				chAnswer(a, 'config.hidetext', false);
			} else {
				NSchange(a, 'config.hidetext', false);
			}
		}
		if(k == 'isVertical') v = (v == 'single') ? true : false;
		if(k == 'image' && v === null) {
			v = '';
			NSchange(a, 'isVertical', true);
			chAnswer(a, 'config.hidetext', false);
		}

		if(k == 'full_image' && v === null) v = '';
		if(k == 'full_image') k = 'fullimage';
		if(k == 'config.star_count' && a.config.default_rate > v) NSchange(a, 'config.default_rate', v);

		if(k == 'config.img_on') {
			NSchange(a, 'config.img_on', v);
			NSchange(a, 'config.img_off', v + '-empty');

			props.dispatch({
				aid:answer_id,
				k:'config.img_on',
				qid:question.Question.id,
				type:'CHANGEANSWER',
				v,
			});

			props.dispatch({
				aid:answer_id,
				k:'config.img_off',
				qid:question.Question.id,
				type:'CHANGEANSWER',
				v:`${v}-empty`,
			});
		}

		if(k == 'answer') {
			NSchange(a, 'answer_st', v);

			props.dispatch({
				aid:answer_id,
				k:'answer_st',
				qid:question.Question.id,
				type:'CHANGEANSWER',
				v,
			});
		}

		if(k == 'config.list_type' && v != 'list') {
			NSchange(a, 'config.first_empty', false);
		}

		// when enabling star rating disable the score
		if(k == 'config.scorepoints') {
			NSchange(a, 'correct', 0);
		}

		if(multiple.current) {
			const q = {
				Answer:[],
				Question:_question.Question,
			};

			for(let i in _question.Answer) {
				q.Answer.push(NSchange(_question.Answer[i], k, v, true));
				props.dispatch({
					k,
					aid:_question.Answer[i].id,
					qid:_question.Answer[i].question_id,
					type:'CHANGEANSWER',
					v,
				});
			}
		} else {
			const b = NSchange(a, k, v, true);

			for(let i in _question.Answer) {
				if(_question.Answer[i].id != answer_id) continue;
				_question.Answer[i] = b;
				break;
			}

			props.dispatch({
				aid:answer_id,
				k,
				qid:question.Question.id,
				type:'CHANGEANSWER',
				v,
			});
		}

		setQuestion(_question);
	};

	const onSortEnd = (oldIndex, newIndex, question_id) => {
		props.dispatch({
			newIndex:newIndex,
			oldIndex:oldIndex,
			qid:question_id,
			type:'SORTANSWER',
		});

		const _question = { ...question };
		_question.Answer = arrayMove(_question.Answer, oldIndex, newIndex);

		for(let i in _question.Answer) {
			_question.Answer[i].pos = i;
		}
		setQuestion(_question);

		preventSelection(false);
	};

	const deleteAnswer = (answer) => {
		props.dispatch({
			aid:answer.id,
			qid:props.question.Question.id,
			type:'DELETEANSWER',
		});
		
		const _answers = props.question.Answer.filter((a) => a.id != answer.id);
		props.question.Answer = _answers;

		setQuestion({ ...question, Answer:_answers });

		$.post('/quizs/deleteanswer', { id:answer.id });
	};

	const handleFeedback = (field) => {
		if(props.question.Question != '') {
			setFeedback(field);

			if(field == 'feedback') {
				props.dispatch({
					k:'show_feedback',
					qid:props.question.Question.id,
					type:'CHANGEQUESTION',
					v:true,
				});

				props.dispatch({
					k:'isRight',
					qid:props.question.Question.id,
					type:'CHANGEQUESTION',
					v:true,
				});
			}

			if(field == 'config.feedback_wrong') {
				props.dispatch({
					k:'show_feedback',
					qid:props.question.Question.id,
					type:'CHANGEQUESTION',
					v:true,
				});

				props.dispatch({
					k:'isRight',
					qid:props.question.Question.id,
					type:'CHANGEQUESTION',
					v:false,
				});
			}
		}
	};

	const image_hook = () => {
		if(props.saveQuiz != undefined) props.saveQuiz();
	};

	const actual_question = question.Question;
	const answers = question.Answer;

	actual_question.pointsxanswer = (actual_question.pointsxanswer == false || actual_question.pointsxanswer == '0') ? '0' : '1';

	const has_media = (isNotEmpty(actual_question, 'image') || isNotEmpty(actual_question, 'video')) ? true : false;

	const video_url = actual_question?.video?.url ?? '';

	const question_types = [
		{ k:'1', v:"Single answer" },
		{ k:'2', v:"Multiple answers" },
		{ k:'3', v:"Sortable answers" },
	];

	const question_scoring_type = [
		{ k:'1', v:"Give the points for each correct answer selected" },
		{ k:'0', v:"Give points if all correct answers are selected" },
	];

	const allowMulti = (answers.filter((a) => a.config.type != 'closed_text').length == 0) ? true : false;

	let answers_row = parseInt(actual_question?.answers_row ?? '2');
	if(answers_row < 1) answers_row = 2;

	return (
		<div>
			{(!props.onlyAnswer) && (
				<div>
					<ButtonCollection _class={{ other:'submenu_builder_sidebar' }}>
						<SecondaryButton
							icon='knk-arrow-l-big'
							_href={`#${base}questions`}
						>Back to questions</SecondaryButton>
					</ButtonCollection>

					<Model
						model={actual_question}
						change={changeQuestion}
					>
						<RichEditor
							name="question"
							has_emojis={true}
							_class={{ first:true }}
						>Question text</RichEditor>

						<MediaUploader
							auto_url={`/quizs/auto_img/${actual_question.promotion_id}`}
							auto_type={'question'}
							imageField="image"
							videoField="video"
							hooks={image_hook}
							image={actual_question.fullimage}
							video={video_url}
							_size={{ fit:'auto' }}
							_thumb_size={{ width:'100%', height:150 }}
						>Upload media</MediaUploader>

						{(has_media) && (
							<MatrixSelector
								name="isVertical"
								value={(actual_question.isVertical) ? 'single' : 'double'}
								_default_value='single'
								allow='photo_orientation'
							>Position</MatrixSelector>
						)}
					</Model>
				</div>
			)}

			{(props.onlyAnswer) && (
				<ButtonCollection _class={{ other:'submenu_builder_sidebar' }}>
					<SecondaryButton
						icon='knk-arrow-l-big'
						_href={`#${base}questions/${actual_question.id}`}
					>Back to questions</SecondaryButton>
				</ButtonCollection>
			)}

			<div class="gui_not_in_top_big">
				<BuilderSubSectionTitle
					title="Answers"
					bordered={false}
					title_actions={(actual_question.is_advanced == '0') ? (
						<MKButton
							icon={false}
							onKeyMouse={(e) => keymouseQuestion(e, { type:'closed_text' })}
							onMousePaste={(type, text) => mousePasteAnswer(type, text, { type:'closed_text' })}
							onClick={() => addAnswer({ type:'closed_text' })}
						>Add answers</MKButton>
					) : (
						<FloatingSelectorList
							button_label="Add answers"
							_elements={fields}
						>
							<MenuItem />
						</FloatingSelectorList>
					)}
				/>

				<SortableFields
					items={answers}
					intl={props.intl}
					changeAnswer={(answer_id, k, v) => changeAnswer(answer_id, k, v)}
					onSortStart={() => preventSelection(true)}
					onSortEnd={({ oldIndex, newIndex }) => onSortEnd(oldIndex, newIndex, actual_question.id)}
					actions={{
						edit:editAnswer,
						delete:deleteAnswer,
						copy:copyAnswer,
						keymouseQuestion,
						mousePasteAnswer,
					}}
					question={actual_question}
					open_answer={open_answer}
					useDragHandle={true}
					image_hook={image_hook}
				/>
			</div>

			<article class="gui_not_in_top_huge">
				<BuilderSubSectionTitle
					title="Advanced options"
					icon='knk-settings'
				/>

				<Model
					model={actual_question}
					change={changeQuestion}
				>
					<TextArea
						name="feedback"
						onFocus={() => handleFeedback('feedback')}
						label_props={{
							tooltip:<Tooltip>Add a customized feedback message for each individual question. Use it to show the correct answer to the question or add an explanation</Tooltip>,
						}}
					>Feedback message text</TextArea>

					{((isNotEmpty(actual_question.config, 'feedback_wrong')) || show_wrong) ? (
						<TextArea
							name="config.feedback_wrong"
							onFocus={() => handleFeedback('config.feedback_wrong')}
							value={actual_question.config.feedback_wrong}
							label_props={{
								tooltip:<Tooltip>Add a customized feedback message for each individual question. Use it to show the correct answer to the question or add an explanation</Tooltip>,
							}}
						>Feedback message for incorrect answer</TextArea>
					) : (
						<ActionLink
							onClick={() => setShowWrong(true)}
						>Use a different feedback message for an incorrect answer</ActionLink>
					)}

					{(has_media) && (
						<Checkbox
							name="config.hidequestion"
						>Hide Question text</Checkbox>
					)}

					<Range
						min={1}
						max={Math.min((answers ?? []).length, 10)}
						name="answers_row"
						value={answers_row}
						default={2}
						_class={{ size:40 }}
					>Container columns</Range>

					{(answers_row > 1) && (
						<Checkbox
							name="config.force_desktop_layout"
						>Do not force 1 column-view for mobile devices</Checkbox>
					)}

					{(allowMulti && actual_question.is_advanced) && (
						<Select
							name="type"
							label_props={{
								tooltip:<Tooltip>Define if the user can select one single option, multiple options or let them order the options</Tooltip>,
							}}
							values={question_types}
							_class={{ size:50 }}
						>Question type</Select>
					)}
				</Model>

				{(allowMulti && actual_question.type != '1' && answers.length > 0) && (
					<Model
						model={actual_question}
						change={changeQuestion}
					>
						<Range
							min={0}
							max={answers.length}
							name="min_answers"
							value={parseInt(actual_question.min_answers)}
							default={0}
							_class={{size:40}}
						>Minimum number of answers</Range>

						<Range
							min={Math.min(answers.length, actual_question.min_answers)}
							max={answers.length}
							name="max_answers"
							value={(actual_question.max_answers != undefined) ? parseInt(actual_question.max_answers) : answers.length}
							default={answers.length}
							_class={{size:40}}
						>Maximum number of answers</Range>
					</Model>
				)}

				{(actual_question.type == '1') && (
					<DisabledArea disabled={(isNotEmpty(actual_question, 'feedback')) ? true : false}>
						<Model
							model={actual_question.config}
							change={changeQuestionConfig}
						>
							<Checkbox
								name="hidecontinue"
								label_props={{
									tooltip:<Tooltip>Check this option if you want the user to move to the next question by just clicking on the answer buttons. By default, the Continue button is displayed in closed answers to allow users to move forward to the next questions of the quiz</Tooltip>,
								}}
							>Hide Continue button</Checkbox>
						</Model>
					</DisabledArea>
				)}

				{(actual_question.type != '1' || ensureBoolean(actual_question?.is_advanced ?? '0')) && (
					<Model
						model={actual_question}
						change={changeQuestion}
					>
						<Select
							name="pointsxanswer"
							value={actual_question.pointsxanswer}
							label_props={{
								tooltip:<Tooltip>Define if the user can select one single option, multiple options or let them order the options</Tooltip>,
							}}
							values={question_scoring_type}
						>Select the scoring system</Select>

						{(actual_question.pointsxanswer == 0) && (
							<Input
								value={actual_question.config.question_points}
								name="config.question_points"
							>How many points?</Input>
						)}
					</Model>
				)}
			</article>
		</div>
	)
};

export default injectIntl(QuestionOptions);